import Cookies from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';
import captureException from 'src/common/utils/captureException';
import loadRecaptchaSdk, { RECAPTCHA_COOKIE_NAME } from 'src/common/utils/loadRecaptchaSdk';
import retry from 'src/common/utils/retry';

const GRECAPTCHA_ERROR = 'grecaptcha_error';

type Grecaptcha = typeof grecaptcha;
/**
 * @see https://cloud.google.com/recaptcha-enterprise/docs/actions
 * The action name is validated by backend
 */
type GrecaptchaAction =
| 'login'
| 'password_reset'
| 'signup'
| 'direct_instabook_guest_checkout';

/**
 * A hook to load grecaptcha SDK onMount, and callback to renew the token and set cookie.
 * @returns a callback that will have access to grecaptcha state.
 * - Success: will set a cookie with the auth token.
 * - Fail: throws an error with unknown reason, or with error.message === grecaptchaErrorMessage
 * @param action The type of action being performed.
 * @param shouldLoadRecaptcha Whether to load the recaptcha SDK on mount.
 */
const useGrecaptcha = (
  action: GrecaptchaAction,
  shouldLoadRecaptcha: boolean = true,
): {
    /** This callback will have access to grecaptcha state, set by loadRecaptchaSdk */
    renewRecaptchaAndSetCookie: () => Promise<void>;
    /** Used to compare error.message in catch handlers, to return a grecaptcha final-form error */
    grecaptchaErrorMessage: typeof GRECAPTCHA_ERROR;
  } => {
  const [grecaptcha, setGrecaptcha] = useState<Grecaptcha | null>(null);

  // Load recaptcha when the hook is mounted
  useEffect(() => {
    if (!shouldLoadRecaptcha) return;
    loadRecaptchaSdk(({ grecaptcha: grecaptchaObject }: any) => setGrecaptcha(grecaptchaObject));
  }, [shouldLoadRecaptcha]);

  const renewRecaptchaAndSetCookie = useCallback(
    async () => {
      if (!grecaptcha) {
        throw new Error(GRECAPTCHA_ERROR);
      }

      return retry(1, async () => {
        await grecaptcha?.enterprise
          .execute(process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY || '', { action }).then(
            (token: string) => {
              Cookies.set(RECAPTCHA_COOKIE_NAME, token);
            },
            (reason: unknown) => {
              throw reason; // unknown reason; will not use our error message.
            },
          );
      }, async () => {
        captureException(new Error('Retry fallback executed'));
        throw new Error(GRECAPTCHA_ERROR);
      });
    },
    [action, grecaptcha],
  );

  return {
    renewRecaptchaAndSetCookie,
    grecaptchaErrorMessage: GRECAPTCHA_ERROR,
  };
};

export default useGrecaptcha;
